// SSE 客户端 / Server-Sent Events client — typed event dispatch plus exponential-backoff reconnect.
// 使用说明：构造时传入 url 和按事件类型分派的 handlers；connect() 连接，close() 永久断开。
// Usage: construct with url + per-type handlers; call connect() to start, close() to stop permanently.

type EventHandler<E> = (event: E) => void;

export class SseClient<E = unknown> {
  private source: EventSource | null = null;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  constructor(
    private readonly url: string,
    private readonly handlers: Record<string, EventHandler<E>>,
  ) {}

  // 建立 SSE 连接并绑定事件分派 / Open the SSE stream and attach per-type listeners.
  connect(): void {
    if (this.closed) return;
    this.source = new EventSource(this.url, { withCredentials: true });
    for (const [type, fn] of Object.entries(this.handlers)) {
      this.source.addEventListener(type, (e: MessageEvent) => {
        try {
          fn(JSON.parse(e.data) as E);
        } catch {
          /* swallow bad JSON; SSE still alive */
        }
      });
    }
    this.source.onerror = () => this.scheduleReconnect();
  }

  // 指数退避重连 / Schedule an exponential-backoff reconnect, capped at 30s.
  private scheduleReconnect(): void {
    if (this.closed) return;
    const delay = Math.min(1000 * 2 ** this.attempt, 30_000);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.source?.close();
      this.connect();
    }, delay);
  }

  // 永久关闭并阻止后续重连 / Permanently close the stream and block future reconnects.
  close(): void {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.source?.close();
  }
}
